import { useEffect, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, Star, AlertCircle } from 'lucide-react';
import { useAuth } from '@/lib/auth';

export default function ReviewsManagement() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('REVIEWS', 'edit');
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ customerName: '', rating: 5, comment: '', isVisible: true });

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.getReviews();
      setReviews(res.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Impossible de charger les avis');
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ customerName: '', rating: 5, comment: '', isVisible: true });
    setShowDialog(true);
  };

  const openEdit = (review) => {
    setEditing(review);
    setForm({
      customerName: review.customerName || '',
      rating: review.rating || 5,
      comment: review.comment || '',
      isVisible: review.isVisible !== false,
    });
    setShowDialog(true);
  };

  const save = async () => {
    if (!form.customerName.trim()) {
      toast.error('Le nom du client est requis');
      return;
    }
    if (form.rating < 1 || form.rating > 5) {
      toast.error('La note doit être comprise entre 1 et 5');
      return;
    }

    try {
      if (editing) {
        await adminApi.updateReview(editing.id, form);
        toast.success('Avis modifié');
      } else {
        await adminApi.createReview(form);
        toast.success('Avis créé');
      }
      setShowDialog(false);
      load();
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const toggleVisibility = async (review) => {
    if (!canEdit) return;
    try {
      const newStatus = !review.isVisible;
      await adminApi.updateReview(review.id, { isVisible: newStatus });
      setReviews(prev => prev.map(r => r.id === review.id ? { ...r, isVisible: newStatus } : r));
      toast.success(newStatus ? 'Avis visible sur le site' : 'Avis masqué');
    } catch (err) {
      toast.error('Erreur lors de la mise à jour de la visibilité');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cet avis ?')) return;
    try {
      await adminApi.deleteReview(id);
      load();
      toast.success('Avis supprimé');
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Gestion des Avis">
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-garden-800 border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout title="Gestion des Avis">
        <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900 rounded-xl p-6 text-center text-red-600 dark:text-red-400">
          <AlertCircle size={24} className="mx-auto mb-2" />
          <p>{error}</p>
          <Button onClick={load} className="mt-4 bg-red-600 hover:bg-red-700 text-white rounded-full">Réessayer</Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Gestion des Avis Clients">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <p className="text-sm text-charcoal/60 dark:text-zinc-400">
            Gérez les avis clients affichés sur le site vitrine.
          </p>
          {canEdit && (
            <Button onClick={openNew} className="bg-garden-800 hover:bg-garden-900 text-white rounded-full flex items-center gap-2" data-testid="add-review-btn">
              <Plus size={16} /> Ajouter un avis
            </Button>
          )}
        </div>

        {reviews.length === 0 ? (
          <div className="bg-white dark:bg-zinc-900 rounded-3xl p-12 text-center border border-garden-200 dark:border-zinc-800">
            <p className="text-charcoal/50 dark:text-zinc-500 mb-4">Aucun avis client pour le moment.</p>
            {canEdit && <Button onClick={openNew} variant="outline" className="rounded-full">Créer le premier avis</Button>}
          </div>
        ) : (
          <div className="bg-white dark:bg-zinc-900 rounded-3xl border border-garden-200 dark:border-zinc-800 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-garden-100 dark:border-zinc-800 bg-garden-50/50 dark:bg-zinc-800/40 text-xs font-semibold text-garden-800 dark:text-sunny uppercase tracking-wider">
                    <th className="px-6 py-4">Client</th>
                    <th className="px-6 py-4">Note</th>
                    <th className="px-6 py-4">Commentaire</th>
                    <th className="px-6 py-4">Date</th>
                    <th className="px-6 py-4 text-center">Visibilité</th>
                    {canEdit && <th className="px-6 py-4 text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-garden-100 dark:divide-zinc-800 text-sm text-charcoal dark:text-zinc-300">
                  {reviews.map((r) => (
                    <tr key={r.id} className="hover:bg-garden-50/30 dark:hover:bg-zinc-800/20 transition-colors" data-testid={`review-row-${r.id}`}>
                      <td className="px-6 py-4 font-medium text-garden-900 dark:text-white">{r.customerName}</td>
                      <td className="px-6 py-4">
                        <div className="flex gap-0.5">
                          {[1, 2, 3, 4, 5].map((s) => (
                            <Star key={s} size={14} className={s <= r.rating ? 'text-sunny fill-sunny' : 'text-gray-300 dark:text-zinc-600'} />
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 max-w-xs md:max-w-md truncate">{r.comment || <span className="text-charcoal/30 italic">Pas de commentaire</span>}</td>
                      <td className="px-6 py-4 text-xs text-charcoal/50 dark:text-zinc-500">
                        {r.createdAt ? new Date(r.createdAt).toLocaleDateString('fr-FR') : '-'}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center">
                          <Switch
                            checked={r.isVisible}
                            disabled={!canEdit}
                            onCheckedChange={() => toggleVisibility(r)}
                            data-testid={`review-toggle-${r.id}`}
                          />
                        </div>
                      </td>
                      {canEdit && (
                        <td className="px-6 py-4 text-right space-x-2 whitespace-nowrap">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(r)} className="hover:text-garden-800 dark:hover:text-sunny rounded-full" data-testid={`edit-btn-${r.id}`}>
                            <Edit size={16} />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => remove(r.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-full" data-testid={`delete-btn-${r.id}`}>
                            <Trash2 size={16} />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Dialog Création / Édition */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg bg-white dark:bg-zinc-900 border dark:border-zinc-800 text-charcoal dark:text-zinc-100 rounded-3xl" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading text-garden-800 dark:text-sunny font-semibold">
              {editing ? 'Modifier l\'avis client' : 'Ajouter un avis client'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="customerName" className="text-sm font-medium">Nom du client *</Label>
              <Input
                id="customerName"
                value={form.customerName}
                onChange={(e) => setForm(prev => ({ ...prev, customerName: e.target.value }))}
                className="border-garden-200 dark:border-zinc-700 rounded-xl"
                placeholder="Ex: Jean Dupont"
                data-testid="review-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium block">Note *</Label>
              <div className="flex gap-2 items-center py-1">
                {[1, 2, 3, 4, 5].map((stars) => (
                  <button
                    key={stars}
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, rating: stars }))}
                    className="hover:scale-110 transition-transform"
                    data-testid={`rating-star-${stars}`}
                  >
                    <Star
                      size={28}
                      className={stars <= form.rating ? 'text-sunny fill-sunny' : 'text-gray-300 dark:text-zinc-700'}
                    />
                  </button>
                ))}
                <span className="text-xs text-charcoal/50 dark:text-zinc-500 ml-2">({form.rating} sur 5)</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="comment" className="text-sm font-medium">Commentaire</Label>
              <Textarea
                id="comment"
                value={form.comment}
                onChange={(e) => setForm(prev => ({ ...prev, comment: e.target.value }))}
                className="border-garden-200 dark:border-zinc-700 rounded-xl min-h-[100px]"
                placeholder="Ex: Très bon accueil et repas excellent, je recommande !"
                data-testid="review-comment-textarea"
              />
            </div>

            <div className="flex items-center justify-between border-t border-garden-100 dark:border-zinc-800 pt-4">
              <div className="space-y-0.5">
                <Label htmlFor="isVisible" className="text-sm font-medium">Visible sur le site</Label>
                <p className="text-xs text-charcoal/50 dark:text-zinc-500">Si actif, cet avis apparaîtra en ligne.</p>
              </div>
              <Switch
                id="isVisible"
                checked={form.isVisible}
                onCheckedChange={(checked) => setForm(prev => ({ ...prev, isVisible: checked }))}
                data-testid="review-visible-switch"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-full">
              Annuler
            </Button>
            <Button onClick={save} className="bg-garden-800 hover:bg-garden-900 text-white rounded-full" data-testid="save-review-btn">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
